// Generic broker trade parser — auto-detects columns from CSV/TSV/pasted rows.
// Tuned for TradeLocker-style exports (Symbol, Side, Qty, Open/Close Price, P&L)
// but matches synonyms from MT4/5, cTrader and most broker dashboards.

const COLUMN_SYNONYMS = {
  pair: ["symbol", "pair", "instrument", "market", "ticker", "asset", "product"],
  direction: ["side", "direction", "type", "action", "position", "buy/sell", "order type", "trade type"],
  lot_size: ["qty", "quantity", "lots", "lot", "lot size", "volume", "size", "amount", "contracts", "units"],
  entry: ["open price", "entry price", "entry", "avg open", "price open", "open", "avg entry price", "opening price", "avg. price", "entry avg"],
  exit: ["close price", "exit price", "exit", "avg close", "price close", "close", "avg exit price", "closing price", "exit avg"],
  pnl: ["p&l", "p/l", "pl", "pnl", "profit", "net profit", "net p&l", "net p/l", "gain", "realized p&l", "realized pnl", "profit/loss", "result"],
};

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9/&\s.]/g, "").trim();

function matchColumn(header) {
  const h = norm(header);
  if (!h) return null;
  for (const [field, names] of Object.entries(COLUMN_SYNONYMS)) {
    if (names.includes(h)) return field;
  }
  for (const [field, names] of Object.entries(COLUMN_SYNONYMS)) {
    if (names.some((n) => h.includes(n))) return field;
  }
  return null;
}

function detectDelimiter(line) {
  if (line.includes("\t")) return "\t";
  const semis = (line.match(/;/g) || []).length;
  const commas = (line.match(/,/g) || []).length;
  return semis > commas ? ";" : ",";
}

function splitLine(line, delim) {
  // minimal quoted-CSV support
  const cells = [];
  let cur = "";
  let inQ = false;
  for (const ch of line) {
    if (ch === '"') inQ = !inQ;
    else if (ch === delim && !inQ) { cells.push(cur); cur = ""; }
    else cur += ch;
  }
  cells.push(cur);
  return cells.map((c) => c.trim());
}

function parseNumber(v) {
  if (v === undefined || v === null) return "";
  let s = String(v).trim();
  if (!s || s === "-" || s === "—") return "";
  const negParen = /^\(.*\)$/.test(s);
  s = s.replace(/[()\s]/g, "").replace(/[$€£¥]/g, "").replace(/[A-Za-z%]+$/g, "");
  // 1.234,56 (EU) vs 1,234.56 (US)
  if (/,\d{1,2}$/.test(s) && !s.includes(".")) s = s.replace(/\./g, "").replace(",", ".");
  else s = s.replace(/,/g, "");
  const n = parseFloat(s);
  if (Number.isNaN(n)) return "";
  return String(negParen ? -n : n);
}

function parseDirection(v) {
  const s = norm(v);
  if (!s) return "";
  if (s.startsWith("buy") || s.startsWith("long") || s === "b") return "long";
  if (s.startsWith("sell") || s.startsWith("short") || s === "s") return "short";
  return "";
}

function normalizePair(v) {
  return String(v || "").trim().toUpperCase().replace(/\.[A-Z0-9]+$/, "");
}

export function parseBrokerData(text) {
  const errors = [];
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { trades: [], errors: ["Nothing to parse — paste rows or upload a CSV."] };

  const delim = detectDelimiter(lines[0]);
  const headerCells = splitLine(lines[0], delim);
  const mapping = {};
  headerCells.forEach((h, idx) => {
    const field = matchColumn(h);
    if (field && !(field in mapping)) mapping[field] = idx;
  });

  let dataLines = lines;
  let positional = false;
  if (mapping.pair !== undefined && mapping.direction !== undefined) {
    dataLines = lines.slice(1);
  } else {
    // headerless fallback: pair, side, lots, entry, exit, pnl
    positional = true;
    const probe = splitLine(lines[0], delim);
    if (probe.length < 4 || !parseDirection(probe[1])) {
      return {
        trades: [],
        errors: [
          "Couldn't detect columns. Include a header row (e.g. Symbol, Side, Qty, Open Price, Close Price, P&L) or use the order: pair, side, lots, entry, exit, pnl.",
        ],
      };
    }
  }

  const get = (cells, field, posIdx) =>
    positional ? cells[posIdx] : mapping[field] !== undefined ? cells[mapping[field]] : "";

  const trades = [];
  dataLines.forEach((line, i) => {
    const cells = splitLine(line, delim);
    const pair = normalizePair(get(cells, "pair", 0));
    const direction = parseDirection(get(cells, "direction", 1));
    if (!pair || !direction) {
      errors.push(`Row ${i + 1}: skipped — needs a symbol and a buy/sell side.`);
      return;
    }
    trades.push({
      pair,
      direction,
      lot_size: parseNumber(get(cells, "lot_size", 2)),
      entry: parseNumber(get(cells, "entry", 3)),
      exit: parseNumber(get(cells, "exit", 4)),
      pnl: parseNumber(get(cells, "pnl", 5)),
    });
  });

  if (!trades.length && !errors.length) errors.push("No trade rows found.");
  return { trades, errors };
}
